import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface ModuleChapter {
  id: string;
  module_id: string;
  chapter_number: number;
  title: string;
  order_index: number;
  book_label: string | null;
  icon_url: string | null;
  created_at: string | null;
}

/**
 * Every module_chapters column EXCEPT pdf_text.
 *
 * pdf_text holds the full extracted text of the chapter PDF (20–40 KB per chapter)
 * and is only used server-side by the AI tools. Fetching it with select('*') made
 * the Surgery module page download about 1.3–1.6 MB of hidden text on every open
 * (load test, 27 Sep 2026). Use this list for any chapter query the app shows.
 */
export const CHAPTER_COLUMNS =
  'id, module_id, chapter_number, title, order_index, created_at, book_label, enable_sections, icon_url, pdf_url, pdf_pages, pdf_uploaded_at, case_count, created_by';

// Fetch all chapters for a module
export function useModuleChapters(moduleId?: string) {
  return useQuery({
    queryKey: ['module-chapters', moduleId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('module_chapters')
        .select(CHAPTER_COLUMNS)
        .eq('module_id', moduleId!)
        .order('order_index', { ascending: true });

      if (error) throw error;
      return data as ModuleChapter[];
    },
    enabled: !!moduleId,
  });
}

// Fetch a single chapter
export function useChapter(chapterId?: string) {
  return useQuery({
    queryKey: ['chapter', chapterId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('module_chapters')
        .select(CHAPTER_COLUMNS)
        .eq('id', chapterId!)
        .maybeSingle();

      if (error) throw error;
      return data as ModuleChapter | null;
    },
    enabled: !!chapterId,
  });
}
